import React, { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { usePermissions } from '../../context/PermissionContext';
import { getRoleLabel } from '../../utils/roleLabels';
import { getInitials, getAvatarColorClass } from '../../utils/avatar';
import PageTitle from '../PageTitle';
import { API_BASE_URL } from '../../services/api';
import { canSeeStatements } from '../../config/featureFlags';

const Layout = () => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { hasPermission, isStaff } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();


  // Apply horizontal layout and fluid layout to body
  useEffect(() => {
    document.body.classList.add('layout-horizontal', 'layout-fluid');
    return () => {
      document.body.classList.remove('layout-horizontal', 'layout-fluid');
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation menu items - using proper Tabler structure with <ul> and <li>
  const navItems = (
    <ul className="navbar-nav">
      <li className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
        <Link to="/" className="nav-link">
          <span className="nav-link-icon">
            {/* Tabler IconHomeFilled */}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-home">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12.707 2.293l9 9c.63 .63 .184 1.707 -.707 1.707h-1v6a3 3 0 0 1 -3 3h-1v-7a3 3 0 0 0 -2.824 -2.995l-.176 -.005h-2a3 3 0 0 0 -3 3v7h-1a3 3 0 0 1 -3 -3v-6h-1c-.89 0 -1.337 -1.077 -.707 -1.707l9 -9a1 1 0 0 1 1.414 0m.293 11.707a1 1 0 0 1 1 1v7h-4v-7a1 1 0 0 1 .883 -.993z" />
            </svg>
          </span>
          <span className="nav-link-title">Dashboard</span>
        </Link>
      </li>
      <li className={`nav-item ${location.pathname === '/invoices' ? 'active' : ''}`}>
        <Link to="/invoices" className="nav-link">
          <span className="nav-link-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-file-invoice">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005zm4 15h-2a1 1 0 0 0 0 2h2a1 1 0 0 0 0 -2m0 -4h-8a1 1 0 0 0 0 2h8a1 1 0 0 0 0 -2m-7 -7h-1a1 1 0 1 0 0 2h1a1 1 0 1 0 0 -2" />
              <path d="M19 7h-4l-.001 -4.001z" />
            </svg>
          </span>
          <span className="nav-link-title">Invoices</span>
        </Link>
      </li>
      <li className={`nav-item ${location.pathname === '/credit-notes' ? 'active' : ''}`}>
        <Link to="/credit-notes" className="nav-link">
          <span className="nav-link-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-file-description">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005zm3 14h-6a1 1 0 0 0 0 2h6a1 1 0 0 0 0 -2m0 -4h-6a1 1 0 0 0 0 2h6a1 1 0 0 0 0 -2" />
              <path d="M19 7h-4l-.001 -4.001z" />
            </svg>
          </span>
          <span className="nav-link-title">Credit Notes</span>
        </Link>
      </li>
      {canSeeStatements(user?.role, settings) && (
        <li className={`nav-item ${location.pathname === '/statements' ? 'active' : ''}`}>
          <Link to="/statements" className="nav-link">
            <span className="nav-link-icon">
              {/* Tabler IconFileAnalyticsFilled */}
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-file-analytics">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005zm-3 10a1 1 0 0 0 -1 1v5a1 1 0 0 0 2 0v-5a1 1 0 0 0 -1 -1m3 4a1 1 0 0 0 -1 1v1a1 1 0 0 0 2 0v-1a1 1 0 0 0 -1 -1m3 -2a1 1 0 0 0 -1 1v3a1 1 0 0 0 2 0v-3a1 1 0 0 0 -1 -1" />
                <path d="M19 7h-4l-.001 -4.001z" />
              </svg>
            </span>
            <span className="nav-link-title">Statements</span>
          </Link>
        </li>
      )}
      {/* Unallocated - GA, Admin, Manager */}
      {hasPermission('UNALLOCATED_VIEW') && (
        <li className={`nav-item ${location.pathname === '/unallocated' ? 'active' : ''}`}>
          <Link to="/unallocated" className="nav-link">
            <span className="nav-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-zoom-question">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M14 3.072a8 8 0 0 1 2.32 11.834l5.387 5.387a1 1 0 0 1 -1.414 1.414l-5.388 -5.387a8 8 0 0 1 -12.905 -6.32l.005 -.285a8 8 0 0 1 11.995 -6.643m-4 8.928a1 1 0 0 0 -.993 .883l-.007 .127a1 1 0 0 0 1.993 .117l.007 -.127a1 1 0 0 0 -1 -1m-1.9 -5.123a1 1 0 0 0 1.433 1.389l.088 -.09a.5 .5 0 1 1 .379 .824a1 1 0 0 0 -.002 2a2.5 2.5 0 1 0 -1.9 -4.123" />
              </svg>
            </span>
            <span className="nav-link-title">Unallocated</span>
          </Link>
        </li>
      )}
      {/* Suppliers Dropdown - Staff roles (only if module enabled) */}
      {settings?.suppliersEnabled !== false && isStaff() && (
        <li className={`nav-item dropdown ${location.pathname.startsWith('/suppliers') || location.pathname.startsWith('/supplier-') ? 'active' : ''}`}>
          <a 
            className="nav-link dropdown-toggle" 
            href="#navbar-suppliers" 
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            role="button" 
            aria-expanded="false"
          >
            <span className="nav-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-building-warehouse" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M3 21v-13l9 -4l9 4v13" />
                <path d="M13 13h4v8h-10v-6h6" />
                <path d="M13 17h4" />
              </svg>
            </span>
            <span className="nav-link-title">Suppliers</span>
          </a>
          <div className="dropdown-menu">
            <Link className={`dropdown-item ${location.pathname === '/suppliers' ? 'active' : ''}`} to="/suppliers">
              Manage Suppliers
            </Link>
            <Link className={`dropdown-item ${location.pathname === '/supplier-invoices' ? 'active' : ''}`} to="/supplier-invoices">
              Invoices
            </Link>
            <Link className={`dropdown-item ${location.pathname === '/supplier-credit-notes' ? 'active' : ''}`} to="/supplier-credit-notes">
              Credit Notes
            </Link>
            <Link className={`dropdown-item ${location.pathname === '/supplier-statements' ? 'active' : ''}`} to="/supplier-statements">
              Statements
            </Link>
          </div>
        </li>
      )}
      {/* Companies - Staff roles */}
      {hasPermission('COMPANIES_VIEW') && (
        <li className={`nav-item ${location.pathname === '/companies' ? 'active' : ''}`}>
          <Link to="/companies" className="nav-link">
            <span className="nav-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-briefcase">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M22 13.478v4.522a3 3 0 0 1 -3 3h-14a3 3 0 0 1 -3 -3v-4.522l.553 .277a20.999 20.999 0 0 0 18.897 -.002l.55 -.275zm-8 -11.478a3 3 0 0 1 3 3v1h2a3 3 0 0 1 3 3v2.242l-1.447 .724a19.002 19.002 0 0 1 -16.726 .186l-.647 -.32l-1.18 -.59v-2.242a3 3 0 0 1 3 -3h2v-1a3 3 0 0 1 3 -3h4zm-2 8a1 1 0 0 0 -1 1a1 1 0 1 0 2 .01c0 -.562 -.448 -1.01 -1 -1.01zm2 -6h-4a1 1 0 0 0 -1 1v1h6v-1a1 1 0 0 0 -1 -1z" />
              </svg>
            </span>
            <span className="nav-link-title">Companies</span>
          </Link>
        </li>
      )}
      {/* Users - GA, Admin, Manager */}
      {hasPermission('USERS_VIEW') && (
        <li className={`nav-item ${location.pathname === '/users' ? 'active' : ''}`}>
          <Link to="/users" className="nav-link">
            <span className="nav-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-user">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 2a5 5 0 1 1 -5 5l.005 -.217a5 5 0 0 1 4.995 -4.783z" />
                <path d="M14 14a5 5 0 0 1 5 5v1a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-1a5 5 0 0 1 5 -5h4z" />
              </svg>
            </span>
            <span className="nav-link-title">Users</span>
          </Link>
        </li>
      )}
      {/* Activity Logs - GA, Admin */}
      {hasPermission('ACTIVITY_LOGS_VIEW') && (
        <li className={`nav-item ${location.pathname === '/activity-logs' ? 'active' : ''}`}>
          <Link to="/activity-logs" className="nav-link">
            <span className="nav-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-library">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M18.333 2a3.667 3.667 0 0 1 3.667 3.667v8.666a3.667 3.667 0 0 1 -3.667 3.667h-8.666a3.667 3.667 0 0 1 -3.667 -3.667v-8.666a3.667 3.667 0 0 1 3.667 -3.667zm-4.333 10h-3a1 1 0 0 0 0 2h3a1 1 0 0 0 0 -2m3 -3h-6a1 1 0 0 0 0 2h6a1 1 0 0 0 0 -2m-1 -3h-5a1 1 0 0 0 0 2h5a1 1 0 0 0 0 -2" />
                <path d="M3.517 6.391a1 1 0 0 1 .99 1.738c-.313 .178 -.506 .51 -.507 .868v10c0 .548 .452 1 1 1h10c.284 0 .405 -.088 .626 -.486a1 1 0 0 1 1.748 .972c-.546 .98 -1.28 1.514 -2.374 1.514h-10c-1.652 0 -3 -1.348 -3 -3v-10.002a3 3 0 0 1 1.517 -2.605" />
              </svg>
            </span>
            <span className="nav-link-title">Activity Logs</span>
          </Link>
        </li>
      )}
    </ul>
  );

  // Disable autocomplete globally for all forms (except login)
  useEffect(() => {
    // More aggressive autocomplete prevention for Chrome
    const disableAutocomplete = () => {
      // Only target forms/inputs within the Layout (not login page)
      const pageWrapper = document.querySelector('.page:not(.page-center)');
      if (!pageWrapper) return;
      
      // Skip if on auth pages
      const isAuthPage = window.location.pathname.includes('/login') ||
                        window.location.pathname.includes('/forgot-password') ||
                        window.location.pathname.includes('/reset-password') ||
                        window.location.pathname.includes('/change-password');
      
      if (isAuthPage) return;
      
      const forms = pageWrapper.querySelectorAll('form');
      const passwordInputs = pageWrapper.querySelectorAll('input[type="password"]');
      const textInputs = pageWrapper.querySelectorAll('input[type="text"], input[type="email"]');
      const allInputs = pageWrapper.querySelectorAll('input, textarea, select');
      
      forms.forEach(form => {
        // Skip if already has autocomplete attribute or is on auth pages
        if (!form.hasAttribute('autocomplete') && !form.closest('.page-center')) {
          form.setAttribute('autocomplete', 'off');
          form.setAttribute('data-form-type', 'other'); // Additional hint for Chrome
        }
      });
      
      // For password fields, use "new-password" which Chrome respects better
      passwordInputs.forEach(input => {
        if (!input.hasAttribute('autocomplete') && !input.closest('.page-center')) {
          input.setAttribute('autocomplete', 'new-password');
          // Add readonly trick - Chrome won't autofill readonly fields
          // Only apply if field is empty
          if (!input.value && !input.hasAttribute('readonly') && !input.hasAttribute('data-readonly-handled')) {
            input.setAttribute('readonly', 'readonly');
            input.setAttribute('data-readonly-handled', 'true');
            input.style.caretColor = 'auto'; // Ensure cursor shows
            
            // Remove readonly on focus
            const handleFocus = (e) => {
              e.target.removeAttribute('readonly');
            };
            
            // Remove readonly on mousedown (before focus) for better UX
            const handleMouseDown = (e) => {
              e.target.removeAttribute('readonly');
            };
            
            input.addEventListener('focus', handleFocus, { once: true });
            input.addEventListener('mousedown', handleMouseDown, { once: true });
          }
        }
      });
      
      // For text/email inputs, use "off"
      textInputs.forEach(input => {
        if (!input.hasAttribute('autocomplete') && !input.closest('.page-center')) {
          input.setAttribute('autocomplete', 'off');
        }
      });
      
      // For other inputs
      allInputs.forEach(input => {
        if (input.type !== 'password' && 
            input.type !== 'text' && 
            input.type !== 'email' &&
            !input.hasAttribute('autocomplete') && 
            !input.closest('.page-center')) {
          input.setAttribute('autocomplete', 'off');
        }
      });
    };
    
    // Run on mount and after route changes
    const timer = setTimeout(disableAutocomplete, 100);
    
    // Also disable for dynamically added inputs (debounced)
    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(disableAutocomplete, 150);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => {
      clearTimeout(timer);
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [location]);

  return (
    <div className="page" autoComplete="off">
      <PageTitle />
      {/* Navbar - Logo, Menu, Notifications, User Menu all inline */}
      <header className="navbar navbar-expand-md d-print-none">
        <div className="container-fluid">
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbar-menu"
            aria-controls="navbar-menu"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          {/* Logo */}
          <div className="navbar-brand navbar-brand-autodark me-3">
            <Link to="/" className="navbar-brand-image">
              {settings?.logoLight ? (
                <img 
                  src={`${API_BASE_URL}${settings.logoLight}`} 
                  alt={settings.siteTitle || settings.siteName || 'Logo'} 
                  style={{ maxHeight: '32px' }}
                />
              ) : (
                <h3 className="mb-0">{settings?.siteTitle || settings?.siteName || 'Makita Invoice Portal'}</h3>
              )}
            </Link>
          </div>

          {/* Navigation Menu - Inline with logo, collapses on mobile */}
          <div className="collapse navbar-collapse" id="navbar-menu">
            {navItems}
          </div>

          {/* Right side menu items - User */}
          <div className="navbar-nav flex-row order-md-last ms-auto">
            {/* User Dropdown */}
            <div className="nav-item dropdown">
              <Link
                to="#"
                className="nav-link d-flex lh-1 text-reset"
                data-bs-toggle="dropdown"
                aria-label="Open user menu"
                onClick={(e) => e.preventDefault()}
              >
                {user?.avatar ? (
                  <span className="avatar avatar-sm" style={{ backgroundImage: `url(${API_BASE_URL}${user.avatar})` }}></span>
                ) : (
                  <span className={`avatar avatar-sm ${getAvatarColorClass(user?.name)} text-white`}>
                    {getInitials(user?.name)}
                  </span>
                )}
                <div className="d-none d-xl-block ps-2">
                  <div>{user?.name || 'User'}</div>
                  <div className="mt-1 small text-secondary">{getRoleLabel(user?.role) || 'User'}</div>
                </div>
              </Link>
              <div className="dropdown-menu dropdown-menu-end dropdown-menu-arrow">
                <Link to="/profile" className="dropdown-item">My Profile</Link>
                {hasPermission('IMPORT_DATA_VIEW') && (
                  <Link to="/import-data" className="dropdown-item">Import Data</Link>
                )}
                {hasPermission('SETTINGS_VIEW') && (
                  <Link to="/settings" className="dropdown-item">Settings</Link>
                )}
                {hasPermission('TEMPLATES_VIEW') && (
                  <Link to="/templates" className="dropdown-item">Customer Templates</Link>
                )}
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item">Logout</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="page-wrapper">
        {/* Page header is handled by individual pages, not here */}
        <div className="page-body">
          <div className="container-fluid">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
